import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import type { EmailWithError } from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/sections/InviteSection/utils/consts";
import { EMAIL_VALIDATION_ERROR } from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/sections/InviteSection/utils/consts";
import { swSettings } from "app/common/services/swSettings";
import { checkEmailInputs } from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/sections/InviteSection/utils/checkEmailInputs";
import type { IInvitation } from "app/pages/digital-suite/home-page/components/Rewards/utils/types";
import { allTrackers } from "app/services/track/track";
import { EVENTS_CATEGORY } from "app/pages/digital-suite/home-page/components/Rewards/utils/consts";
import { inviteUsersToRegister } from "app/pages/digital-suite/home-page/components/InviteUsers/InviteUsersApi";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { i18nFilter } from "app/filters/i18nFilter";

interface UseInviteSectionProps {
    invitationsList: IInvitation[];
    setInvitationsList: (invitations: IInvitation[]) => void;
}

export const useInviteSection = ({ invitationsList, setInvitationsList }: UseInviteSectionProps) => {
    const [emailChips, setEmailChips] = useState<Array<string>>([]);
    const [invalidEmails, setInvalidEmails] = useState<Array<EmailWithError>>([]);
    const [hasChipsError, setHasChipsError] = useState(false);
    const [isInviteLoading, setIsInviteLoading] = useState(false);

    const { showSuccessToast } = useToasts();
    const i18n = i18nFilter();

    const inputRef = useRef(null);
    const userEmail = swSettings?.user?.username;

    useEffect(() => {
        if (invalidEmails.length === 0) {
            setHasChipsError(false);
        }
    }, [invalidEmails]);

    const getEmailsStatus = (chips: string[]): { validEmails: string[]; invalidEmails: EmailWithError[] } => {
        const newInvalidEmails = checkEmailInputs(chips, userEmail);
        const invalidEmailStrings = newInvalidEmails.map((item) => item.email);
        const validEmails = chips
            .filter((email) => !invalidEmailStrings.includes(email))
            .map((email) => email.toLowerCase());
        return { validEmails, invalidEmails: newInvalidEmails };
    };

    const clearInput = () => inputRef.current && inputRef.current.clearValue();

    const sendInvites = async (
        emails: string[],
    ): Promise<{ invalidEmailsList: string[]; isValidationError: boolean }> => {
        const { firstname, lastname } = swSettings.user;

        try {
            const invalidEmailsList = await inviteUsersToRegister(`${firstname} ${lastname}`, emails, emails);
            return { invalidEmailsList, isValidationError: false };
        } catch (error) {
            const isValidationError = error?.status === 400;
            return { invalidEmailsList: emails, isValidationError };
        }
    };

    const processInvitations = async (validEmails: string[], invalidChips: EmailWithError[]) => {
        let alreadyInvitedEmailsWithError: EmailWithError[] = [];

        if (validEmails && validEmails.length > 0) {
            const { invalidEmailsList, isValidationError } = await sendInvites(validEmails);

            alreadyInvitedEmailsWithError = invalidEmailsList.map((email: string) => ({
                email,
                reason: isValidationError
                    ? EMAIL_VALIDATION_ERROR.WRONG_FORMAT
                    : EMAIL_VALIDATION_ERROR.ALREADY_SUBSCRIBED,
            }));
        }

        const allInvalidEmailsWithError = [...invalidChips, ...alreadyInvitedEmailsWithError];
        const allInvalidEmails = allInvalidEmailsWithError.map((item) => item.email);
        const invalidEmailErrors = allInvalidEmailsWithError.map((item) => i18n(item.reason));

        const todayString = dayjs().format("YYYY-MM-DD");
        const newInvitations: IInvitation[] = validEmails
            .filter((email) => !allInvalidEmails.includes(email))
            .map((email) => ({
                Email: email,
                Status: "pending",
                CreatedAt: todayString,
            }));

        setInvitationsList([...invitationsList, ...newInvitations]);
        const hasInvalidEmails = allInvalidEmails.length > 0;
        const hasNewInvitations = newInvitations.length > 0;

        setInvalidEmails(allInvalidEmailsWithError);
        setEmailChips(allInvalidEmails);
        setHasChipsError(hasInvalidEmails);
        if (hasNewInvitations) {
            showSuccessToast(i18n("rewards_modal.main_screen.invite_section.toast"));
        }

        allTrackers.trackEvent(
            EVENTS_CATEGORY,
            "click",
            `Invite/${newInvitations.length}/${newInvitations.map((invite) => invite.Email).join(",")}`,
        );

        if (hasInvalidEmails) {
            allTrackers.trackEvent(EVENTS_CATEGORY, "click", `invalid mails/${allInvalidEmails}/${invalidEmailErrors}`);
        }
    };

    const handleInviteClick = async () => {
        setIsInviteLoading(true);
        const inputValue = (inputRef.current.getValue() || "").trim();
        const updatedChips = inputValue ? [inputValue, ...emailChips] : [...emailChips];
        const { validEmails, invalidEmails: newInvalidChips } = getEmailsStatus(updatedChips);
        await processInvitations(validEmails, newInvalidChips);
        clearInput();
        setIsInviteLoading(false);
    };

    return {
        emailChips,
        setEmailChips,
        inputRef,
        setInvalidEmails,
        invalidEmails,
        handleInviteClick,
        hasChipsError,
        isInviteLoading,
    };
};
